import { useCallback, useState } from 'react'
import {
  Node,
  Edge,
  Connection,
  EdgeChange,
  NodeChange,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
} from '@xyflow/react'
import { WorkflowNodeData, SidebarDragData } from '../types'
import { getNodeDefaults } from '../data/nodeDefaults'

const getNextNodeId = (): string => `node-${crypto.randomUUID().slice(0, 8)}`

interface UndoEntry {
  nodes: Node[]
  edges: Edge[]
}

export const useWorkflowState = (initialNodes: Node[] = [], initialEdges: Edge[] = []) => {
  const [nodes, setNodes] = useState<Node[]>(initialNodes)
  const [edges, setEdges] = useState<Edge[]>(initialEdges)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [workflowName, setWorkflowName] = useState('Untitled Workflow')
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([])

  const pushUndo = useCallback(() => {
    setUndoStack((prev) => [...prev.slice(-19), { nodes, edges }])
  }, [nodes, edges])

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((nds) => applyNodeChanges(changes, nds))
    },
    [],
  )

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((eds) => applyEdgeChanges(changes, eds))
    },
    [],
  )

  const onConnect = useCallback(
    (connection: Connection) => {
      pushUndo()
      setEdges((eds) => addEdge({ ...connection, type: 'workflow' }, eds))
    },
    [pushUndo],
  )

  const addNode = useCallback(
    (position: { x: number; y: number }, dragData: SidebarDragData): string => {
      pushUndo()
      const id = getNextNodeId()
      const defaults = getNodeDefaults(dragData.nodeType)

      let data: WorkflowNodeData = { ...defaults }

      if (dragData.nodeType === 'trigger' && dragData.triggerType) {
        data = { ...data, triggerType: dragData.triggerType } as WorkflowNodeData
      }

      if (dragData.nodeType === 'tool_call' && dragData.serverId) {
        data = {
          ...data,
          serverId: dragData.serverId,
          serverName: dragData.serverName ?? '',
          toolId: dragData.toolId ?? '',
          toolName: dragData.toolName ?? '',
          label: dragData.toolName ?? 'MCP Tool Call',
        } as WorkflowNodeData
      }

      const newNode: Node = {
        id,
        type: dragData.nodeType,
        position,
        data,
      }

      setNodes((nds) => [...nds, newNode])
      setSelectedNodeId(id)
      return id
    },
    [pushUndo],
  )

  const removeNode = useCallback(
    (nodeId: string) => {
      pushUndo()
      setNodes((nds) => nds.filter((n) => n.id !== nodeId))
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
      if (selectedNodeId === nodeId) {
        setSelectedNodeId(null)
      }
    },
    [pushUndo, selectedNodeId],
  )

  const updateNodeData = useCallback(
    (nodeId: string, updatedData: Partial<WorkflowNodeData>) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId ? { ...n, data: { ...n.data, ...updatedData } } : n,
        ),
      )
    },
    [],
  )

  const renameNode = useCallback(
    (oldId: string, newId: string) => {
      if (!newId || oldId === newId) return
      setNodes((nds) => {
        if (nds.some((n) => n.id === newId)) return nds
        return nds.map((n) => (n.id === oldId ? { ...n, id: newId } : n))
      })
      setEdges((eds) =>
        eds.map((e) => ({
          ...e,
          source: e.source === oldId ? newId : e.source,
          target: e.target === oldId ? newId : e.target,
        })),
      )
      if (selectedNodeId === oldId) {
        setSelectedNodeId(newId)
      }
    },
    [selectedNodeId],
  )

  const selectNode = useCallback((nodeId: string | null) => {
    setSelectedNodeId(nodeId)
  }, [])

  const undo = useCallback(() => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev
      const last = prev[prev.length - 1]
      setNodes(last.nodes)
      setEdges(last.edges)
      return prev.slice(0, -1)
    })
  }, [])

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) ?? null

  return {
    nodes,
    edges,
    selectedNode,
    selectedNodeId,
    workflowName,
    setWorkflowName,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    removeNode,
    updateNodeData,
    selectNode,
    undo,
    renameNode,
    setNodes,
    setEdges,
  }
}
